/**
 * Command Center - Marketing Hub
 * Consolidated view of all campaigns, creatives, and placements
 * with master-detail layout and objective-aware insights
 */

import React, { useState, useCallback } from 'react';
import {
    Zap,
    RefreshCw,
    Calendar,
    Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { CommandCenterProvider, useCommandCenter } from '../contexts/CommandCenterContext';
import { DATE_RANGE_PRESETS, DATE_RANGE_LABELS } from '../utils/helpers';

// Command Center Components
import HealthMetricsBar from '../components/command-center/HealthMetricsBar';
import AIAdvisorPanel from '../components/command-center/AIAdvisorPanel';
import InsightCard from '../components/command-center/InsightCard';
import ActionConfirmationModal from '../components/command-center/ActionConfirmationModal';
import PlatformTabs from '../components/command-center/PlatformTabs';
import CampaignListPanel from '../components/command-center/CampaignListPanel';
import DetailPanel from '../components/command-center/DetailPanel';
import QuickFunnelBuilder from '../components/command-center/QuickFunnelBuilder';

// Funnel Builder Modal
import { CampaignModal } from '../components/funnel-builder';

// Services
import { pauseCampaign, resumeCampaign, updateCampaignBudget } from '../services/campaignActionsService';

// Platform Connection Modal
import PlatformConnectModal from '../components/PlatformConnectModal';

// Inner component that uses CommandCenter context
const CommandCenterContent = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();

    const {
        campaigns,
        insights,
        aggregateMetrics,
        previousAggregateMetrics,
        aggregateTrends,
        previousPeriodData,
        isLoading,
        isRefreshing,
        refreshData,
        syncCampaigns,
        dateRangePreset,
        setDateRangePreset,
        selectCampaign,
        selectedPlatform,
        // Shared advisor state
        advisorMessages,
        advisorGoal,
        advisorChatId,
        updateAdvisorMessages,
        updateAdvisorGoal
    } = useCommandCenter();

    // Action modal state
    const [actionModal, setActionModal] = useState({
        isOpen: false,
        action: null,
        campaign: null,
        loading: false
    });

    // Platform connect modal
    const [showPlatformConnect, setShowPlatformConnect] = useState(false);

    // Funnel builder state
    const [funnelCampaigns, setFunnelCampaigns] = useState([]);
    const [funnelModal, setFunnelModal] = useState({
        isOpen: false,
        mode: 'create',
        data: null
    });
    const [showFunnelBuilder, setShowFunnelBuilder] = useState(true);

    // Calculate aggregate metrics for HealthMetricsBar
    const overallROAS = aggregateMetrics?.spend > 0
        ? (aggregateMetrics?.revenue || 0) / aggregateMetrics.spend
        : 0;

    const activeCampaigns = campaigns.filter(c =>
        c.status?.toUpperCase() === 'ACTIVE'
    ).length;

    const criticalInsights = insights.filter(i => i.priority === 'critical').length;
    const highInsights = insights.filter(i => i.priority === 'high').length;
    const alertCount = criticalInsights + highInsights;

    // Action handlers
    const handleAction = useCallback((action, campaign) => {
        setActionModal({
            isOpen: true,
            action,
            campaign,
            loading: false
        });
    }, []);

    const handleCloseModal = () => {
        setActionModal({
            isOpen: false,
            action: null,
            campaign: null,
            loading: false
        });
    };

    const handleConfirmAction = async (params = {}) => {
        const { action, campaign } = actionModal;
        if (!campaign || !currentUser || !selectedBrand) return;

        setActionModal(prev => ({ ...prev, loading: true }));

        try {
            const campaignId = campaign.platformCampaignId || campaign.id;
            const platform = campaign.platform || 'meta';

            switch (action) {
                case 'pause':
                    await pauseCampaign(campaignId, platform, currentUser.uid, selectedBrand.id);
                    break;
                case 'resume':
                    await resumeCampaign(campaignId, platform, currentUser.uid, selectedBrand.id);
                    break;
                case 'scale':
                    await updateCampaignBudget(
                        campaignId,
                        platform,
                        params.amount,
                        'daily',
                        currentUser.uid,
                        selectedBrand.id
                    );
                    break;
                case 'analyze':
                    // Navigate to creative analyzer with campaign context
                    navigate(`/creative-analyzer?campaign=${campaignId}`);
                    handleCloseModal();
                    return;
            }

            console.log(`Action ${action} completed successfully`);
            handleCloseModal();
            refreshData();

        } catch (error) {
            console.error(`Error executing ${action}:`, error);
            alert(`Failed to ${action} campaign: ${error.message}`);
        } finally {
            setActionModal(prev => ({ ...prev, loading: false }));
        }
    };

    // Insight action handlers
    const handleInsightClick = (insight) => {
        if (insight.campaignId) {
            selectCampaign(insight.campaignId);
        }
    };

    const handlePauseAction = (insight) => {
        const campaign = campaigns.find(c => c.id === insight.campaignId);
        if (campaign) {
            handleAction('pause', campaign);
        }
    };

    const handleResumeAction = (insight) => {
        const campaign = campaigns.find(c => c.id === insight.campaignId);
        if (campaign) {
            handleAction('resume', campaign);
        }
    };

    const handleScaleAction = (insight) => {
        const campaign = campaigns.find(c => c.id === insight.campaignId);
        if (campaign) {
            handleAction('scale', campaign);
        }
    };

    // Funnel builder handlers
    const handleAddFunnelCampaign = (stage) => {
        setFunnelModal({
            isOpen: true,
            mode: 'create',
            data: { funnelStage: stage, platform: selectedPlatform || 'meta' }
        });
    };

    const handleEditFunnelCampaign = (campaign) => {
        setFunnelModal({
            isOpen: true,
            mode: 'edit',
            data: campaign
        });
    };

    const handleDeleteFunnelCampaign = (campaignId) => {
        setFunnelCampaigns(prev => prev.filter(c => c.id !== campaignId));
    };

    const handleSaveFunnelCampaign = (data) => {
        if (funnelModal.mode === 'edit' && funnelModal.data?.id) {
            // Update existing
            setFunnelCampaigns(prev => prev.map(c =>
                c.id === funnelModal.data.id ? { ...c, ...data } : c
            ));
        } else {
            // Create new
            const newCampaign = {
                id: `funnel_${Date.now()}`,
                ...data,
                status: 'draft',
                createdAt: new Date().toISOString()
            };
            setFunnelCampaigns(prev => [...prev, newCampaign]);
        }
        setFunnelModal({ isOpen: false, mode: 'create', data: null });
    };

    if (!selectedBrand) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                gap: 'var(--space-4)'
            }}>
                <Zap size={48} style={{ color: 'var(--color-text-tertiary)' }} />
                <h2 style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                    Select a brand to view your Command Center
                </h2>
                <p style={{ color: 'var(--color-text-tertiary)', margin: 0 }}>
                    Choose a brand from the sidebar to see actionable insights
                </p>
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'calc(100vh - 64px)', // Adjust based on your header height
            overflow: 'auto'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-4) var(--space-6)',
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                flexShrink: 0
            }}>
                <div>
                    <h1 style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: 700,
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)'
                    }}>
                        <Zap size={24} style={{ color: 'var(--color-primary)' }} />
                        Command Center
                    </h1>
                    <p style={{
                        color: 'var(--color-text-tertiary)',
                        margin: 'var(--space-1) 0 0 0',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        Your marketing hub - manage campaigns, analyze performance, take action
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                    {/* Date Range Selector */}
                    <select
                        value={dateRangePreset}
                        onChange={(e) => setDateRangePreset(e.target.value)}
                        disabled={isLoading || isRefreshing}
                        className="select-field"
                        style={{
                            width: 'auto',
                            padding: 'var(--space-2) var(--space-8) var(--space-2) var(--space-3)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        {Object.entries(DATE_RANGE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>

                    {/* Sync Button - Forces fresh data from ad platforms */}
                    <button
                        onClick={syncCampaigns}
                        disabled={isRefreshing}
                        className="btn btn-ghost"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-2) var(--space-3)'
                        }}
                        title="Sync fresh data from your ad platforms"
                    >
                        <RefreshCw size={18} className={isRefreshing ? 'spin' : ''} />
                        {isRefreshing ? 'Syncing...' : 'Sync'}
                    </button>

                    {/* Create Campaign Button */}
                    <button
                        onClick={() => navigate('/campaign-builder')}
                        className="btn btn-primary"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-2) var(--space-4)'
                        }}
                    >
                        <Plus size={18} />
                        Create Campaign
                    </button>
                </div>
            </div>

            {/* Platform Tabs */}
            <div style={{
                padding: '0 var(--space-6)',
                background: 'var(--color-surface)',
                flexShrink: 0
            }}>
                <PlatformTabs onConnectPlatform={() => setShowPlatformConnect(true)} />
            </div>

            {/* Health Metrics Bar */}
            <div style={{
                padding: 'var(--space-4) var(--space-6)',
                background: 'var(--color-surface)',
                borderBottom: '1px solid var(--color-border)',
                flexShrink: 0
            }}>
                <HealthMetricsBar
                    metrics={aggregateMetrics || { spend: 0, revenue: 0, impressions: 0, clicks: 0, conversions: 0 }}
                    overallROAS={overallROAS}
                    activeCampaigns={activeCampaigns}
                    totalCampaigns={campaigns.length}
                    alertCount={alertCount}
                    loading={isLoading}
                    trends={aggregateTrends}
                />
            </div>

            {/* AI Advisor Panel (Collapsible) */}
            <div style={{
                padding: 'var(--space-4) var(--space-6)',
                background: 'var(--color-background)',
                flexShrink: 0
            }}>
                <AIAdvisorPanel
                    insights={insights}
                    metrics={aggregateMetrics}
                    previousMetrics={previousAggregateMetrics}
                    aggregateTrends={aggregateTrends}
                    previousPeriodData={previousPeriodData}
                    overallROAS={overallROAS}
                    loading={isLoading}
                    campaigns={campaigns}
                    dateRangeLabel={DATE_RANGE_LABELS[dateRangePreset] || 'this period'}
                    userGoal={advisorGoal}
                    onSetGoal={updateAdvisorGoal}
                    messages={advisorMessages}
                    onMessagesChange={updateAdvisorMessages}
                    chatId={advisorChatId}
                    userId={currentUser?.uid}
                    brandId={selectedBrand?.id}
                />
            </div>

            {/* Actionable Insights (Horizontal Scrollable) */}
            {insights.length > 0 && (
                <div style={{
                    padding: 'var(--space-4) var(--space-6)',
                    background: 'var(--color-background)',
                    borderBottom: '1px solid var(--color-border)',
                    flexShrink: 0
                }}>
                    <h3 style={{
                        margin: '0 0 var(--space-3) 0',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--color-text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        Actionable Insights
                        {alertCount > 0 && (
                            <span style={{
                                background: 'var(--color-error)',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-full)',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 600
                            }}>
                                {alertCount} urgent
                            </span>
                        )}
                    </h3>
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-3)',
                        overflowX: 'auto',
                        paddingBottom: 'var(--space-2)',
                        marginRight: '-var(--space-6)',
                        paddingRight: 'var(--space-6)'
                    }}>
                        {insights.slice(0, 6).map(insight => (
                            <div key={insight.id} style={{ minWidth: '380px', maxWidth: '380px' }}>
                                <InsightCard
                                    insight={insight}
                                    onAction={() => handleInsightClick(insight)}
                                    onPause={handlePauseAction}
                                    onResume={handleResumeAction}
                                    onScale={handleScaleAction}
                                    compact
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Quick Funnel Builder */}
            {showFunnelBuilder && (
                <div style={{
                    padding: 'var(--space-4) var(--space-6)',
                    background: 'var(--color-background)',
                    borderBottom: '1px solid var(--color-border)',
                    flexShrink: 0
                }}>
                    <QuickFunnelBuilder
                        campaigns={funnelCampaigns.filter(c =>
                            !selectedPlatform || c.platform === selectedPlatform
                        )}
                        onAddCampaign={handleAddFunnelCampaign}
                        onEditCampaign={handleEditFunnelCampaign}
                        onDeleteCampaign={handleDeleteFunnelCampaign}
                        activePlatform={selectedPlatform}
                    />
                </div>
            )}

            {/* Main Content - Master-Detail Layout */}
            <div style={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden'
            }}>
                {/* Campaign List Panel (Left) */}
                <CampaignListPanel />

                {/* Detail Panel (Right) */}
                <DetailPanel onAction={handleAction} />
            </div>

            {/* Action Confirmation Modal */}
            <ActionConfirmationModal
                isOpen={actionModal.isOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirmAction}
                action={actionModal.action}
                entity={actionModal.campaign ? {
                    type: 'campaign',
                    id: actionModal.campaign.id,
                    name: actionModal.campaign.name,
                    platform: actionModal.campaign.platform
                } : null}
                metrics={actionModal.campaign?.currentMetrics}
                loading={actionModal.loading}
            />

            {/* Platform Connect Modal */}
            <PlatformConnectModal
                isOpen={showPlatformConnect}
                onClose={(success) => {
                    setShowPlatformConnect(false);
                    if (success) {
                        // Refresh data after successful connection
                        refreshData();
                    }
                }}
                brand={selectedBrand}
            />

            {/* Funnel Campaign Modal */}
            <CampaignModal
                isOpen={funnelModal.isOpen}
                onClose={() => setFunnelModal({ isOpen: false, mode: 'create', data: null })}
                onSave={handleSaveFunnelCampaign}
                mode={funnelModal.mode}
                initialData={funnelModal.data}
            />

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Custom scrollbar for insights */
                div::-webkit-scrollbar {
                    height: 6px;
                }
                div::-webkit-scrollbar-track {
                    background: var(--color-border);
                    border-radius: 3px;
                }
                div::-webkit-scrollbar-thumb {
                    background: var(--color-text-tertiary);
                    border-radius: 3px;
                }
                div::-webkit-scrollbar-thumb:hover {
                    background: var(--color-text-secondary);
                }
            `}</style>
        </div>
    );
};

// Main export with Provider wrapper
const CommandCenter = () => {
    return (
        <CommandCenterProvider>
            <CommandCenterContent />
        </CommandCenterProvider>
    );
};

export default CommandCenter;
